import { useState } from "react";
import { Link } from "react-router-dom";
import "./BoxScore.css";

const COLS = ["MIN", "PTS", "FG", "3PT", "FT", "REB", "AST", "TO", "STL", "BLK", "OREB", "DREB", "PF", "+/-"];
const logo = (code) => (code ? `https://a.espncdn.com/i/teamlogos/nba/500/${code.toLowerCase()}.png` : null);
const pct = (v) => {
  const [m, a] = String(v || "").split("-").map(Number);
  return a ? `${Math.round((m / a) * 100)}%` : "";
};
const foulClass = (pf) => (pf >= 6 ? "bx-pf-out" : pf >= 5 ? "bx-pf-5" : pf === 4 ? "bx-pf-4" : pf === 3 ? "bx-pf-3" : "");

function Cell({ c, v }) {
  if (c === "PF") return <td className={foulClass(Number(v))}>{v}{Number(v) >= 6 && <em>OUT</em>}</td>;
  if (c === "+/-") return <td className={String(v).startsWith("+") && v !== "+0" ? "bx-pos" : String(v).startsWith("-") ? "bx-neg" : ""}>{v}</td>;
  return <td className={c === "PTS" ? "bx-pts" : ""}>{v}</td>;
}

const num = (c, v) => {
  if (v == null || v === "—" || v === "") return -Infinity;
  if (["FG", "3PT", "FT"].includes(c)) return Number(String(v).split("-")[0]) || 0;
  return Number(String(v).replace("+", "")) || 0;
};
const sortPlayers = (players, sort) => {
  if (!sort) return players;
  const played = players.filter((p) => !p.dnp);
  played.sort((a, b) => (num(sort.col, b.stats[sort.col]) - num(sort.col, a.stats[sort.col])) * sort.dir);
  return [...played, ...players.filter((p) => p.dnp)];
};

function Rows({ title, players, gameId, sort, onSort }) {
  return (
    <>
      <tr className="bx-group"><th className="bx-name">{title}</th>{COLS.map((c) => (
        <th key={c} className={sort?.col === c ? "is-sorted" : ""}>
          <button type="button" onClick={() => onSort(c)} data-testid={`bx-sort-${c}`} aria-label={`Sort by ${c}`}>
            {c}{sort?.col === c && <span>{sort.dir === 1 ? "▼" : "▲"}</span>}
          </button>
        </th>
      ))}</tr>
      {sortPlayers(players, sort).map((p) => (
        <tr key={p.id} data-testid={`bx-player-${p.id}`}>
          <td className="bx-name">
            <Link to={`/player/${p.id}?game=${gameId}`}>
              <span className="bx-photo">{p.photo && <img src={p.photo} alt="" loading="lazy" />}</span>
              <span>{p.short || p.name}</span>{p.jersey && <small>#{p.jersey}</small>}
            </Link>
          </td>
          {p.dnp ? <td colSpan={COLS.length} className="bx-dnp">{p.reason ? p.reason.toLowerCase() : "Has not entered game"}</td>
            : COLS.map((c) => <Cell key={c} c={c} v={p.stats[c] ?? "—"} />)}
        </tr>
      ))}
    </>
  );
}

function TeamBox({ side, code, name, box, gameId, sort, onSort }) {
  const starters = box.players.filter((p) => p.starter);
  const bench = box.players.filter((p) => !p.starter);
  const t = box.totals || {};
  return (
    <div className="bx-team" data-testid={`bx-team-${side}`}>
      <h4><img src={logo(code)} alt="" />{name || code}<small>{side === "home" ? "HOME" : "AWAY"}</small></h4>
      <div className="bx-scroll">
        <table>
          <tbody>
            <Rows title="STARTERS" players={starters} gameId={gameId} sort={sort} onSort={onSort} />
            <Rows title="BENCH" players={bench} gameId={gameId} sort={sort} onSort={onSort} />
            <tr className="bx-total" data-testid={`bx-totals-${side}`}><td className="bx-name">TEAM</td>{COLS.map((c) => <td key={c}>{["MIN", "+/-"].includes(c) ? "" : t[c]}</td>)}</tr>
            <tr className="bx-pct"><td className="bx-name" />{COLS.map((c) => <td key={c}>{["FG", "3PT", "FT"].includes(c) ? pct(t[c]) : ""}</td>)}</tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function BoxScore({ box, game }) {
  // Tap a column: highest first; tap again: lowest first; third tap: back to ESPN order.
  const [sort, setSort] = useState(null);
  const onSort = (col) => setSort((s) => (s?.col !== col ? { col, dir: 1 } : s.dir === 1 ? { col, dir: -1 } : null));
  if (!box?.away && !box?.home) return <p className="tv-empty">Box score not available yet.</p>;
  return (
    <div className="bx-root" data-testid="tv-boxscore">
      {[["away", game.away_code, game.away_name], ["home", game.home_code, game.home_name]].map(([side, code, name]) => box[side] && (
        <TeamBox key={side} side={side} code={code} name={name} box={box[side]} gameId={game.id} sort={sort} onSort={onSort} />
      ))}
      <p className="bx-foot">+/- = team&apos;s net points while the player is on the court. PF ring: 3 grey · 4 pink · 5 red · 6 fouled out.</p>
    </div>
  );
}
